/**
 * SKOP Alerts Panel
 *
 * UI for creating and managing price alerts.
 */

import { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import {
  createAlert,
  cancelAlert,
  deleteAlert,
  getActiveAlertsForToken,
  getAlerts,
  formatAlertType,
  formatAlertStatus,
  requestNotificationPermission,
  type PriceAlert,
  type AlertType,
} from '../lib/alerts';

interface AlertsPanelProps {
  tokenAddress: string;
  tokenSymbol: string;
  tokenName: string;
  currentPrice: number;
  onAlertCreated?: () => void;
}

function AlertTypeIcon({ iconType }: { iconType: string }) {
  const iconProps = { width: 12, height: 12, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };

  const icons: Record<string, React.ReactNode> = {
    'up': (
      <svg {...iconProps}>
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
        <polyline points="17 6 23 6 23 12"/>
      </svg>
    ),
    'down': (
      <svg {...iconProps}>
        <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
        <polyline points="17 18 23 18 23 12"/>
      </svg>
    ),
    'arrow-up': (
      <svg {...iconProps}>
        <line x1="12" y1="19" x2="12" y2="5"/>
        <polyline points="5 12 12 5 19 12"/>
      </svg>
    ),
    'arrow-down': (
      <svg {...iconProps}>
        <line x1="12" y1="5" x2="12" y2="19"/>
        <polyline points="19 12 12 19 5 12"/>
      </svg>
    ),
  };

  return (
    <span style={{ width: '12px', height: '12px', display: 'inline-flex' }}>
      {icons[iconType] || null}
    </span>
  );
}

export function AlertsPanel({
  tokenAddress,
  tokenSymbol,
  tokenName,
  currentPrice,
}: AlertsPanelProps) {
  const { publicKey } = useWallet();
  const walletAddress = publicKey?.toString() ?? null;

  const [alerts, setAlerts] = useState<PriceAlert[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [alertType, setAlertType] = useState<AlertType>('above');
  const [targetValue, setTargetValue] = useState<string>('');
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default');

  // Load alerts
  useEffect(() => {
    if (walletAddress) {
      const tokenAlerts = getActiveAlertsForToken(walletAddress, tokenAddress);
      setAlerts(tokenAlerts);
    }
  }, [walletAddress, tokenAddress]);

  // Check notification permission
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  const handleRequestPermission = async () => {
    const granted = await requestNotificationPermission();
    setNotificationPermission(granted ? 'granted' : 'denied');
  };

  const handleCreateAlert = () => {
    if (!walletAddress || !targetValue) return;

    const value = parseFloat(targetValue);
    if (isNaN(value) || value <= 0) return;

    const isPercentType = alertType === 'percent_up' || alertType === 'percent_down';

    createAlert({
      tokenAddress,
      tokenSymbol,
      tokenName,
      type: alertType,
      targetPrice: isPercentType ? undefined : value,
      targetPercent: isPercentType ? value : undefined,
      currentPrice,
      walletAddress,
    });

    // Refresh alerts
    setAlerts(getActiveAlertsForToken(walletAddress, tokenAddress));
    setShowCreateForm(false);
    setTargetValue('');
  };

  const handleCancelAlert = (alertId: string) => {
    if (!walletAddress) return;
    cancelAlert(walletAddress, alertId);
    setAlerts(getActiveAlertsForToken(walletAddress, tokenAddress));
  };

  const handleDeleteAlert = (alertId: string) => {
    if (!walletAddress) return;
    deleteAlert(walletAddress, alertId);
    setAlerts(getActiveAlertsForToken(walletAddress, tokenAddress));
  };

  // Calculate suggested targets
  const suggestedTargets = {
    above: [
      { label: '+10%', value: currentPrice * 1.1 },
      { label: '+25%', value: currentPrice * 1.25 },
      { label: '+50%', value: currentPrice * 1.5 },
      { label: '2x', value: currentPrice * 2 },
    ],
    below: [
      { label: '-10%', value: currentPrice * 0.9 },
      { label: '-25%', value: currentPrice * 0.75 },
      { label: '-50%', value: currentPrice * 0.5 },
    ],
  };

  if (!walletAddress) {
    return (
      <div style={{
        padding: '16px',
        background: 'var(--bg-secondary)',
        borderRadius: 'var(--radius-lg)',
        textAlign: 'center',
        color: 'var(--text-tertiary)',
      }}>
        Connect wallet to set price alerts
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--bg-secondary)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ width: '16px', height: '16px', display: 'inline-flex', color: 'var(--accent)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
          </span>
          <span style={{ fontWeight: 600 }}>Price Alerts</span>
          {alerts.length > 0 && (
            <span style={{
              padding: '2px 8px',
              background: 'var(--accent-muted)',
              borderRadius: '12px',
              fontSize: '12px',
              color: 'var(--accent)',
            }}>
              {alerts.length}
            </span>
          )}
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          style={{
            padding: '6px 12px',
            background: showCreateForm ? 'var(--bg-tertiary)' : 'var(--accent)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            color: showCreateForm ? 'var(--text-secondary)' : '#000',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {showCreateForm ? 'Cancel' : '+ Add Alert'}
        </button>
      </div>

      {/* Notification Permission Warning */}
      {notificationPermission !== 'granted' && (
        <div style={{
          padding: '12px 16px',
          background: 'rgba(255, 184, 0, 0.1)',
          borderBottom: '1px solid rgba(255, 184, 0, 0.2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{ fontSize: '13px', color: '#ffb800' }}>
            Enable notifications to receive alerts
          </span>
          <button
            onClick={handleRequestPermission}
            style={{
              padding: '4px 12px',
              background: '#ffb800',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              color: '#000',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Enable
          </button>
        </div>
      )}

      {/* Create Alert Form */}
      {showCreateForm && (
        <div style={{
          padding: '16px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-tertiary)',
        }}>
          {/* Alert Type Selection */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              color: 'var(--text-tertiary)',
              marginBottom: '8px',
            }}>
              Alert Type
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[
                { type: 'above' as AlertType, label: 'Price Above', icon: 'up' },
                { type: 'below' as AlertType, label: 'Price Below', icon: 'down' },
                { type: 'percent_up' as AlertType, label: '% Up', icon: 'arrow-up' },
                { type: 'percent_down' as AlertType, label: '% Down', icon: 'arrow-down' },
              ].map(({ type, label, icon }) => (
                <button
                  key={type}
                  onClick={() => {
                    setAlertType(type);
                    setTargetValue('');
                  }}
                  style={{
                    padding: '8px 12px',
                    background: alertType === type ? 'rgba(0, 240, 255, 0.1)' : 'var(--bg-secondary)',
                    border: `1px solid ${alertType === type ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius-md)',
                    color: alertType === type ? 'var(--accent)' : 'var(--text-secondary)',
                    fontSize: '13px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <AlertTypeIcon iconType={icon} />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Target Input */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{
              display: 'block',
              fontSize: '12px',
              color: 'var(--text-tertiary)',
              marginBottom: '8px',
            }}>
              {alertType === 'percent_up' || alertType === 'percent_down'
                ? 'Target Percentage'
                : 'Target Price'}
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {alertType !== 'percent_up' && alertType !== 'percent_down' && (
                <span style={{ color: 'var(--text-tertiary)' }}>$</span>
              )}
              <input
                type="number"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                placeholder={
                  alertType === 'percent_up' || alertType === 'percent_down'
                    ? 'e.g., 25'
                    : `e.g., ${(currentPrice * 1.5).toFixed(6)}`
                }
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                }}
                step="any"
                min="0"
              />
              {(alertType === 'percent_up' || alertType === 'percent_down') && (
                <span style={{ color: 'var(--text-tertiary)' }}>%</span>
              )}
            </div>
          </div>

          {/* Suggested Targets */}
          {(alertType === 'above' || alertType === 'below') && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                color: 'var(--text-tertiary)',
                marginBottom: '8px',
              }}>
                Quick Select
              </label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {suggestedTargets[alertType].map(({ label, value }) => (
                  <button
                    key={label}
                    onClick={() => setTargetValue(value.toFixed(8))}
                    style={{
                      padding: '6px 12px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-secondary)',
                      fontSize: '12px',
                      cursor: 'pointer',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Current Price Reference */}
          <div style={{
            padding: '8px 12px',
            background: 'var(--bg-secondary)',
            borderRadius: 'var(--radius-md)',
            marginBottom: '16px',
            fontSize: '13px',
          }}>
            <span style={{ color: 'var(--text-tertiary)' }}>Current price: </span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
              ${currentPrice.toFixed(8)}
            </span>
            {targetValue && !isNaN(parseFloat(targetValue)) && (
              <>
                <span style={{ color: 'var(--text-tertiary)' }}> → Target: </span>
                <span style={{
                  color: alertType === 'above' || alertType === 'percent_up'
                    ? 'var(--success)'
                    : 'var(--error)',
                  fontWeight: 600,
                }}>
                  {alertType === 'percent_up' || alertType === 'percent_down'
                    ? `${alertType === 'percent_up' ? '+' : '-'}${targetValue}%`
                    : `$${parseFloat(targetValue).toFixed(8)}`}
                </span>
              </>
            )}
          </div>

          {/* Create Button */}
          <button
            onClick={handleCreateAlert}
            disabled={!targetValue || isNaN(parseFloat(targetValue)) || parseFloat(targetValue) <= 0}
            style={{
              width: '100%',
              padding: '12px',
              background: 'var(--accent)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              color: '#000',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              opacity: !targetValue || isNaN(parseFloat(targetValue)) ? 0.5 : 1,
            }}
          >
            Create Alert
          </button>
        </div>
      )}

      {/* Alerts List */}
      {alerts.length > 0 ? (
        <div>
          {alerts.map((alert) => {
            const status = formatAlertStatus(alert.status);
            return (
              <div
                key={alert.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <div>
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--text-primary)',
                    marginBottom: '4px',
                  }}>
                    {formatAlertType(alert)}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--text-tertiary)',
                  }}>
                    Current: ${alert.currentPrice?.toFixed(8) || 'Loading...'}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    padding: '4px 8px',
                    background: `${status.color}20`,
                    borderRadius: 'var(--radius-sm)',
                    color: status.color,
                    fontSize: '11px',
                    fontWeight: 600,
                  }}>
                    {status.label}
                  </span>
                  <button
                    onClick={() => handleCancelAlert(alert.id)}
                    style={{
                      padding: '4px 8px',
                      background: 'none',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--text-tertiary)',
                      fontSize: '11px',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteAlert(alert.id)}
                    style={{
                      padding: '4px 8px',
                      background: 'none',
                      border: 'none',
                      color: 'var(--error)',
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : !showCreateForm ? (
        <div style={{
          padding: '24px 16px',
          textAlign: 'center',
          color: 'var(--text-tertiary)',
        }}>
          <div style={{ width: '24px', height: '24px', margin: '0 auto 8px', color: 'var(--text-tertiary)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
          </div>
          <p style={{ margin: 0, fontSize: '13px' }}>
            No alerts set for {tokenSymbol}
          </p>
          <p style={{ margin: '4px 0 0', fontSize: '12px' }}>
            Get notified when price hits your target
          </p>
        </div>
      ) : null}
    </div>
  );
}

// ============================================================================
// All Alerts List Component
// ============================================================================

export function AllAlertsList() {
  const { publicKey } = useWallet();
  const walletAddress = publicKey?.toString() ?? null;
  const [alerts, setAlerts] = useState<PriceAlert[]>([]);
  const [filter, setFilter] = useState<'all' | 'active' | 'triggered'>('all');

  useEffect(() => {
    if (walletAddress) {
      loadAlerts();
    }
  }, [walletAddress]);

  const loadAlerts = () => {
    if (!walletAddress) return;
    const all = getAlerts(walletAddress);
    setAlerts(all);
  };

  const handleDelete = (alertId: string) => {
    if (!walletAddress) return;
    deleteAlert(walletAddress, alertId);
    loadAlerts();
  };

  const filteredAlerts = alerts.filter(a => {
    if (filter === 'active') return a.status === 'active';
    if (filter === 'triggered') return a.status === 'triggered';
    return true;
  });

  if (!walletAddress) {
    return (
      <div style={{
        padding: '32px',
        textAlign: 'center',
        color: 'var(--text-tertiary)',
      }}>
        Connect wallet to view alerts
      </div>
    );
  }

  return (
    <div>
      {/* Filter Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
      }}>
        {(['all', 'active', 'triggered'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '8px 16px',
              background: filter === f ? 'var(--accent-muted)' : 'var(--bg-secondary)',
              border: `1px solid ${filter === f ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 'var(--radius-md)',
              color: filter === f ? 'var(--accent)' : 'var(--text-secondary)',
              fontSize: '13px',
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {f} ({alerts.filter(a => f === 'all' ? true : a.status === f).length})
          </button>
        ))}
      </div>

      {/* Alerts List */}
      {filteredAlerts.length > 0 ? (
        <div style={{
          background: 'var(--bg-secondary)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}>
          {filteredAlerts.map((alert) => {
            const status = formatAlertStatus(alert.status);
            return (
              <div
                key={alert.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <a
                    href={`/${alert.tokenAddress}`}
                    style={{
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      textDecoration: 'none',
                    }}
                  >
                    {alert.tokenSymbol}
                  </a>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>
                    {formatAlertType(alert)}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    padding: '4px 8px',
                    background: `${status.color}20`,
                    borderRadius: 'var(--radius-sm)',
                    color: status.color,
                    fontSize: '11px',
                    fontWeight: 600,
                  }}>
                    {status.label}
                  </span>
                  <button
                    onClick={() => handleDelete(alert.id)}
                    style={{
                      padding: '4px 8px',
                      background: 'none',
                      border: 'none',
                      color: 'var(--error)',
                      fontSize: '16px',
                      cursor: 'pointer',
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{
          padding: '48px',
          textAlign: 'center',
          color: 'var(--text-tertiary)',
          background: 'var(--bg-secondary)',
          borderRadius: 'var(--radius-lg)',
        }}>
          <div style={{ width: '32px', height: '32px', margin: '0 auto 12px', color: 'var(--text-tertiary)' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
          </div>
          <p style={{ margin: 0 }}>No {filter === 'all' ? '' : filter} alerts</p>
        </div>
      )}
    </div>
  );
}
